import cx from 'clsx';
import React, { useState } from 'react';

import { ReactComponent as AddIcon } from '@assets/svg/add.svg';
import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';
import Button from '@components/uikit/Button/Button';
import FilterSearch from '@components/uikit/FilterSearch/FilterSearch';

import { MODAL_NAMES } from '@constants/modalRenderers';
import { initialSorting } from '@constants/tableSorting';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import { useGpsTrackerQuery } from '@pages/GPSAssetTracking/services/useGPSTrackersQuery';

import { useModalActions } from '@store/useModalStore';

import ContextMenu from '../TrackerContextMenu/TrackerContextMenu';
import GpsTrackersTableSkeleton from './GpsTrackersTableSkeleton';
import style from './style.module.scss';

const GpsTrackerBlock = () => {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState('');
  const [sorting, setSorting] = useState<ITableSorting>(initialSorting);

  const { setActiveModal } = useModalActions();

  const [debouncedValue] = useDebouncedValue(searchValue, 500);

  const { data: gpsTrackerList, isFetching } = useGpsTrackerQuery({ searchValue: debouncedValue, sorting });

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setSorting(initialSorting);
    } else {
      setSorting({ id, desc: true });
    }
  };

  return (
    <div className={style.trackersBlock}>
      <div className={style.trackersBlockHead}>
        <div className={style.trackersBlockHeadTitle}>
          {t('GPS Tracker List')}
          <div className={style.trackersSearchBlock}>
            <FilterSearch
              value={searchValue}
              onChange={(value) => setSearchValue(value || '')}
              variant="table-search"
            />
          </div>
        </div>
        <Button
          text={t('Add new GPS Tracker')}
          onClick={() => setActiveModal({ name: MODAL_NAMES.createEditGpsTrackerModal })}
          type="secondary"
          leftElement={<AddIcon className={style.addIcon} />}
        />
      </div>
      <div className={style.tableWrapper}>
        <table className={style.trackersTable} cellSpacing="0" cellPadding="0">
          <thead className={style.trackersTableHead}>
            <tr>
              <th className={style.trackersTableTh}>
                {t('Model')}
                <TableSort
                  className={style.sortIcon}
                  onClick={() => onChangeSorting('model')}
                  data-tooltip-content="model"
                  data-tooltip-id="trackers-sort-tooltip"
                />
              </th>
              <th className={style.trackersTableTh}>
                {t('Serial Number')}
                <TableSort
                  className={style.sortIcon}
                  onClick={() => onChangeSorting('serialNumber')}
                  data-tooltip-content="serialNumber"
                  data-tooltip-id="trackers-sort-tooltip"
                />
              </th>
              <th className={style.trackersTableTh}>
                {t('IMEI')}
                <TableSort
                  className={style.sortIcon}
                  onClick={() => onChangeSorting('imei')}
                  data-tooltip-content="imei"
                  data-tooltip-id="trackers-sort-tooltip"
                />
              </th>
              <th className={style.trackersTableTh}>
                {t('ICCID')}
                <TableSort
                  className={style.sortIcon}
                  onClick={() => onChangeSorting('iccid')}
                  data-tooltip-content="iccid"
                  data-tooltip-id="trackers-sort-tooltip"
                />
              </th>
              <th className={style.trackersTableTh}>
                {t('Asset Tag')}
                <TableSort
                  className={style.sortIcon}
                  onClick={() => onChangeSorting('asset')}
                  data-tooltip-content="asset"
                  data-tooltip-id="trackers-sort-tooltip"
                />
              </th>
              <th className={style.trackersTableTh}></th>
            </tr>
          </thead>
          <tbody>
            {isFetching ? (
              <GpsTrackersTableSkeleton />
            ) : gpsTrackerList?.length ? (
              gpsTrackerList?.map((item) => (
                <tr key={item.id} className={style.trackersTableBodyRow}>
                  <td className={style.trackersTableTd}>{item.model}</td>
                  <td className={style.trackersTableTd}>{item.serialNumber}</td>
                  <td className={style.trackersTableTd}>{item.imei}</td>
                  <td className={style.trackersTableTd}>{item.iccid}</td>
                  <td className={style.trackersTableTd}>{item.asset?.assetTag}</td>
                  <td
                    className={cx(style.trackersTableTd, style.actionsIconContainer)}
                    data-tooltip-id="table-tracker-menu"
                    data-tooltip-content={JSON.stringify(item)}
                  >
                    <ThreeDotsIcon />
                  </td>
                </tr>
              ))
            ) : (
              <TableEmptyRowExtended
                text={t(!searchValue ? 'You don`t have any GPS Trackers added.' : 'GPS Trackers not found')}
              />
            )}
          </tbody>
        </table>
        <ContextMenu id="table-tracker-menu" />
        <TooltipSort id="trackers-sort-tooltip" sorting={sorting} />
      </div>
    </div>
  );
};

export default GpsTrackerBlock;
